import { motion } from "framer-motion";
import { useTranslation } from "react-i18next";
import { useDemoModal } from "@/components/site/DemoModal";
import {
  ArrowRight,
  Building2,
  Smartphone,
  MapPinned,
  QrCode,
  Languages,
  ShieldCheck,
  MapPin,
  CalendarCheck,
  HeartHandshake,
  Coins,
  Wifi,
  Clock,
  Car,
  Coffee,
  Phone,
  Tv,
  FileText,
  Info,
  Folder,
  Sparkles,
  TrendingUp,
  Map,
  Camera,
  Star,
  NotebookPen,
  Plane,
  Users,
  ConciergeBell,
  DoorOpen,
  UtensilsCrossed,
  BookOpen,
  SlidersHorizontal,
  Megaphone,
  FolderOpen,
} from "lucide-react";
import { Seo } from "@/components/Seo";
import { Reveal } from "@/components/site/Reveal";
import { Btn } from "@/components/site/Btn";
import { LogoMarquee } from "@/components/site/LogoMarquee";
import { DashboardMockup } from "@/components/site/DashboardMockup";
import { CtaSection } from "@/components/site/CtaSection";
import { useLang, lpath } from "@/lib/lang";
import { ASSETS } from "@/config/assets";

const EASE = [0.16, 1, 0.3, 1];

/* Rend un texte avec des retours à la ligne explicites (\n) */
const Lines = ({ text }) => (
  <>
    {String(text).split("\n").map((line, i, arr) => (
      <span key={i}>
        {line}
        {i < arr.length - 1 ? <br /> : null}
      </span>
    ))}
  </>
);

const Overline = ({ children, tone = "blue" }) => {
  const tones = {
    blue: "text-ostelo-blue",
    orange: "text-ostelo-orange",
    green: "text-ostelo-green",
  };
  return <p className={`text-xs font-bold uppercase tracking-[0.2em] ${tones[tone]}`}>{children}</p>;
};

const H2 = ({ children, className = "" }) => (
  <h2 className={`font-sans text-3xl font-extrabold tracking-tight sm:text-4xl ${className}`}>{children}</h2>
);

export default function Home() {
  const { t } = useTranslation();
  const lang = useLang();
  const L = (p) => lpath(lang, p);
  const openDemo = useDemoModal();

  const chips = t("home.hero_chips", { returnObjects: true });
  const apa = t("home.apa", { returnObjects: true });
  const appFeatures = t("home.app_features", { returnObjects: true });
  const servItems = t("home.serv_items", { returnObjects: true });
  const guidePills = t("home.guide_pills", { returnObjects: true });
  const cashSteps = t("home.cash_steps", { returnObjects: true });
  const backFeatures = t("home.back_features", { returnObjects: true });
  const carnetPoints = t("home.carnet_points", { returnObjects: true });
  const perfItems = t("home.perf_items", { returnObjects: true });
  const ecoCols = t("home.eco_cols", { returnObjects: true });
  const ecoBottom = t("home.eco_bottom", { returnObjects: true });

  const APA_ICONS = [CalendarCheck, QrCode, HeartHandshake];
  const APA_TONES = [
    { tag: "bg-ostelo-sky text-ostelo-blue", border: "border-ostelo-blue/15" },
    { tag: "bg-[#FFF1E0] text-[#B96A12]", border: "border-ostelo-orange/20" },
    { tag: "bg-ostelo-mint text-ostelo-green", border: "border-ostelo-green/20" },
  ];
  const APP_ICONS = [ConciergeBell, FileText, Map, DoorOpen, UtensilsCrossed, Star];
  const SERV_ICONS = [Wifi, Clock, Car, Coffee, Phone, Tv, FileText, ConciergeBell, Info, Folder];
  const BACK_ICONS = [BookOpen, SlidersHorizontal, Map, Megaphone, FolderOpen, TrendingUp];
  const CARNET_ICONS = [Camera, MapPin, Star, HeartHandshake];
  const PERF_ICONS = [Sparkles, Clock, TrendingUp, Coins, HeartHandshake, MapPinned];
  const ECO_ICONS = [Building2, Smartphone, MapPinned];
  const ECO_BOTTOM_ICONS = [HeartHandshake, Coins, NotebookPen];

  return (
    <>
      <Seo title={t("home.seo_title")} description={t("home.seo_desc")} lang={lang} />

      {/* 1 — HERO : bleu nuit profond + voyageuse détourée */}
      <section className="relative -mt-[76px] overflow-hidden bg-[radial-gradient(125%_95%_at_60%_30%,#18295F_0%,#0C1B47_52%,#070F2B_100%)] px-6 pb-16 pt-[136px] lg:px-10 lg:pb-24 lg:pt-[150px]" data-testid="hero">
        <div className="relative mx-auto grid max-w-7xl items-center gap-12 lg:grid-cols-2 lg:gap-6">
          <div>
            <motion.p
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.7, ease: EASE }}
              className="inline-flex items-center gap-2.5 rounded-full bg-white/12 px-4 py-2 text-[11px] font-bold uppercase tracking-[0.18em] text-white"
              data-testid="hero-overline"
            >
              <span className="h-1.5 w-1.5 rounded-full bg-ostelo-orange" />
              {t("home.hero_overline")}
            </motion.p>

            <h1 className="mt-7 font-sans text-4xl font-extrabold leading-[1.08] tracking-tight text-white sm:text-5xl lg:text-[3.4rem]">
              <span className="mask-line">
                <motion.span initial={{ y: "110%" }} animate={{ y: 0 }} transition={{ duration: 0.9, delay: 0.15, ease: EASE }}>
                  <Lines text={t("home.hero_title_1")} />
                </motion.span>
              </span>
              <span className="mask-line pb-2">
                <motion.span
                  initial={{ y: "110%" }}
                  animate={{ y: 0 }}
                  transition={{ duration: 0.9, delay: 0.28, ease: EASE }}
                  className="font-sans font-extrabold text-ostelo-orange"
                >
                  <Lines text={t("home.hero_title_2")} />
                </motion.span>
              </span>
            </h1>

            <motion.p
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.5, ease: EASE }}
              className="mt-6 max-w-lg text-base leading-relaxed text-white/75 md:text-lg"
              data-testid="hero-sub"
            >
              {t("home.hero_sub")}
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.62, ease: EASE }}
              className="mt-9 flex flex-wrap items-center gap-4"
            >
              <Btn variant="accent" testId="hero-cta-button" onClick={openDemo}>
                {t("common.cta_demo")}
                <ArrowRight size={16} />
              </Btn>
              <Btn
                to={L("")}
                variant="ghostLight"
                testId="hero-secondary-button"
                onClick={(e) => {
                  e.preventDefault();
                  document.querySelector('[data-testid="apa-section"]')?.scrollIntoView({ behavior: "smooth" });
                }}
              >
                {t("common.cta_discover")}
              </Btn>
            </motion.div>

            <motion.p initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ duration: 1, delay: 0.85 }} className="mt-5 text-xs tracking-wide text-white/50">
              {t("home.hero_note")}
            </motion.p>

            <motion.div
              initial={{ opacity: 0, y: 16 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 0.8, delay: 0.95, ease: EASE }}
              className="mt-8 flex flex-wrap gap-2.5"
            >
              {[QrCode, Languages, ShieldCheck].map((Icon, i) => (
                <span key={i} className="inline-flex items-center gap-2 rounded-full border border-white/20 bg-white/5 px-3.5 py-2 text-[11px] font-semibold text-white/85" data-testid={`hero-chip-${i}`}>
                  <Icon size={13} className="text-ostelo-orange" />
                  {chips[i]}
                </span>
              ))}
            </motion.div>
          </div>

          {/* Voyageuse détourée — PNG officiel (halo + icônes + trajectoire intégrés) */}
          <div className="relative flex h-[360px] min-w-0 items-end justify-center max-lg:overflow-hidden max-md:hidden sm:h-[430px] lg:block lg:h-[600px]" data-testid="hero-visual">
            <motion.img
              src="/assets/characters/voyageuse-hero-972.webp"
              srcSet="/assets/characters/voyageuse-hero-540.webp 540w, /assets/characters/voyageuse-hero-972.webp 972w, /assets/characters/voyageuse-hero-1536.webp 1536w"
              sizes="(min-width: 1024px) 972px, (min-width: 640px) 645px, 90vw"
              width="1536"
              height="1024"
              fetchPriority="high"
              alt={t("home.hero_visual_alt")}
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ duration: 1, delay: 0.35, ease: EASE }}
              className="h-full w-auto max-w-none object-contain object-bottom lg:absolute lg:bottom-[-40px] lg:right-[-160px] lg:h-[108%]"
            />
          </div>
        </div>
      </section>

      {/* 2 — Logos partenaires */}
      <LogoMarquee />

      {/* 3 — AVANT / PENDANT / APRÈS */}
      <section className="bg-white px-6 py-24 lg:px-10 lg:py-28" data-testid="apa-section">
        <div className="mx-auto max-w-7xl">
          <Reveal>
            <Overline tone="blue">{t("home.apa_overline")}</Overline>
            <H2 className="mt-4 max-w-2xl text-ostelo-deep">{t("home.apa_title")}</H2>
            <p className="mt-4 max-w-lg text-base leading-relaxed text-ostelo-ink/60">{t("home.apa_sub")}</p>
          </Reveal>

          <div className="relative mt-14">
            <motion.div
              initial={{ scaleX: 0 }}
              whileInView={{ scaleX: 1 }}
              viewport={{ once: true, margin: "-100px" }}
              transition={{ duration: 1.6, ease: EASE }}
              style={{ transformOrigin: "left" }}
              className="absolute left-[8%] right-[8%] top-[52px] hidden h-0 border-t-2 border-dashed border-ostelo-blue/25 lg:block"
              aria-hidden="true"
            />
            <div className="grid gap-5 lg:grid-cols-3">
              {apa.map((card, i) => {
                const Icon = APA_ICONS[i];
                const tone = APA_TONES[i];
                return (
                  <Reveal key={card.tag} delay={i * 0.12}>
                    <div className={`relative h-full rounded-3xl border bg-white p-8 transition-[box-shadow,transform] duration-500 hover:-translate-y-1.5 hover:shadow-[0_24px_60px_rgb(12,27,71,0.1)] ${tone.border}`} data-testid={`apa-card-${i}`}>
                      <div className="flex items-center justify-between">
                        <span className={`rounded-full px-3.5 py-1.5 text-[10px] font-bold uppercase tracking-[0.15em] ${tone.tag}`}>{card.tag}</span>
                        <Icon size={20} className="text-ostelo-ink/25" />
                      </div>
                      <h3 className="mt-6 font-sans text-2xl font-extrabold text-ostelo-deep">{card.title}</h3>
                      <p className="mt-2.5 text-sm leading-relaxed text-ostelo-ink/60">{card.desc}</p>
                      <ul className="mt-6 space-y-2.5">
                        {card.items.map((item) => (
                          <li key={item} className="flex items-center gap-2.5 text-sm font-medium text-ostelo-ink/75">
                            <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-ostelo-orange" />
                            {item}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </Reveal>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* 4 — GUEST APP : le produit, concrètement */}
      <section className="px-6 pb-24 lg:px-10" data-testid="app-section">
        <div className="relative mx-auto max-w-7xl overflow-hidden rounded-[2.5rem] bg-ostelo-deep px-8 py-16 text-white lg:px-16 lg:py-20">
          <div className="pointer-events-none absolute -right-28 -top-28 h-80 w-80 rounded-full border-[22px] border-ostelo-blue/30" />
          <div className="pointer-events-none absolute -bottom-20 -left-16 h-56 w-56 rounded-full bg-ostelo-blue/20 blur-2xl" />

          <div className="relative grid items-start gap-14 lg:grid-cols-[1.1fr_0.9fr]">
            <div>
              <Reveal>
                <Overline tone="orange">{t("home.app_overline")}</Overline>
                <H2 className="mt-4 text-white">{t("home.app_title")}</H2>
                <p className="mt-4 max-w-lg text-base leading-relaxed text-white/65">{t("home.app_sub")}</p>
              </Reveal>
              <div className="mt-10 grid gap-4 sm:grid-cols-2">
                {appFeatures.map((f, i) => {
                  const Icon = APP_ICONS[i];
                  return (
                    <Reveal key={f.title} delay={0.08 * i}>
                      <div className="h-full rounded-2xl border border-white/10 bg-white/5 p-5 backdrop-blur-sm transition-colors duration-300 hover:bg-white/10" data-testid={`app-feature-${i}`}>
                        <Icon size={18} className="text-ostelo-orange" />
                        <h3 className="mt-3 font-sans text-sm font-bold text-white">{f.title}</h3>
                        <p className="mt-1.5 text-xs leading-relaxed text-white/55">{f.desc}</p>
                      </div>
                    </Reveal>
                  );
                })}
              </div>
            </div>

            <div className="relative">
              <Reveal>
                <img
                  src="/assets/mockups/guest-app-430.webp"
                  srcSet="/assets/mockups/guest-app-430.webp 430w, /assets/mockups/guest-app-860.webp 860w, /assets/mockups/guest-app-1024.webp 1024w"
                  sizes="(min-width: 1024px) 430px, 90vw"
                  width="1024"
                  height="1536"
                  alt={t("home.app_visual_alt")}
                  loading="lazy"
                  className="mx-auto w-full max-w-[430px] object-contain"
                  data-testid="app-visual"
                />
              </Reveal>
            </div>
          </div>
        </div>
      </section>

      {/* 5 — SERVICES & INFORMATIONS DE L'HÔTEL */}
      <section className="bg-[#F6F9FF] px-6 py-24 lg:px-10 lg:py-28" data-testid="serv-section">
        <div className="mx-auto max-w-7xl">
          <div className="grid items-end gap-8 lg:grid-cols-2">
            <Reveal>
              <Overline tone="blue">{t("home.serv_overline")}</Overline>
              <H2 className="mt-4 text-ostelo-deep">{t("home.serv_title")}</H2>
            </Reveal>
            <Reveal delay={0.1}>
              <p className="max-w-md text-base leading-relaxed text-ostelo-ink/60 lg:ml-auto">{t("home.serv_sub")}</p>
            </Reveal>
          </div>

          <div className="mt-12 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-5">
            {servItems.map((item, i) => {
              const Icon = SERV_ICONS[i];
              return (
                <Reveal key={item} delay={i * 0.05}>
                  <div className="flex items-center gap-3 rounded-2xl border border-ostelo-deep/8 bg-white px-4 py-4 transition-[box-shadow,transform] duration-300 hover:-translate-y-0.5 hover:shadow-[0_12px_30px_rgb(12,27,71,0.08)]" data-testid={`serv-chip-${i}`}>
                    <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-ostelo-sky">
                      <Icon size={15} className="text-ostelo-blue" />
                    </span>
                    <span className="text-sm font-bold text-ostelo-deep">{item}</span>
                  </div>
                </Reveal>
              );
            })}
          </div>

          <Reveal delay={0.2} className="mt-8">
            <div className="flex items-start gap-4 rounded-2xl border-l-4 border-ostelo-blue bg-white p-6">
              <Info size={18} className="mt-0.5 shrink-0 text-ostelo-blue" />
              <p className="text-sm leading-relaxed text-ostelo-ink/70" data-testid="serv-note">{t("home.serv_note")}</p>
            </div>
          </Reveal>
        </div>
      </section>

      {/* 6 — CITY GUIDE */}
      <section className="overflow-x-clip bg-white px-6 py-24 lg:px-10 lg:py-28" data-testid="guide-section">
        <div className="mx-auto grid max-w-7xl items-center gap-16 lg:grid-cols-2">
          <div>
            <Reveal>
              <Overline tone="green">{t("home.guide_overline")}</Overline>
              <H2 className="mt-4 text-ostelo-deep">{t("home.guide_title")}</H2>
              <p className="mt-5 max-w-lg text-base leading-relaxed text-ostelo-ink/60">{t("home.guide_sub")}</p>
            </Reveal>
            <Reveal delay={0.15}>
              <div className="mt-8 flex flex-wrap gap-2.5" data-testid="guide-pills">
                {guidePills.map((p, i) => (
                  <span key={p} className="inline-flex items-center gap-2 rounded-full border border-ostelo-deep/10 bg-white px-3.5 py-2 text-[11px] font-semibold text-ostelo-deep/70">
                    <span className={`h-1.5 w-1.5 rounded-full ${i % 3 === 0 ? "bg-ostelo-green" : i % 3 === 1 ? "bg-ostelo-orange" : "bg-ostelo-blue"}`} />
                    {p}
                  </span>
                ))}
              </div>
            </Reveal>
            <Reveal delay={0.25} className="mt-9">
              <Btn
                to={L("")}
                variant="ghost"
                testId="guide-explore-button"
                onClick={(e) => {
                  e.preventDefault();
                  document.querySelector('[data-testid="carnet-section"]')?.scrollIntoView({ behavior: "smooth" });
                }}
              >
                {t("nav.cityguide")}
                <ArrowRight size={16} />
              </Btn>
            </Reveal>
          </div>

          <Reveal delay={0.1}>
            <div className="relative flex items-center justify-center">
              <img
                src="/assets/mockups/city-guide-850.webp"
                srcSet="/assets/mockups/city-guide-500.webp 500w, /assets/mockups/city-guide-850.webp 850w, /assets/mockups/city-guide-1536.webp 1536w"
                sizes="(min-width: 1024px) 840px, 100vw"
                width="1536"
                height="1024"
                alt={t("home.guide_visual_alt")}
                loading="lazy"
                className="w-full object-contain lg:w-[128%] lg:max-w-none xl:w-[138%]"
                data-testid="guide-visual"
              />
            </div>
          </Reveal>
        </div>
      </section>

      {/* 7 — PARTENAIRES & CASH BACK */}
      <section className="overflow-x-clip bg-[#FFF7EC] px-6 py-24 lg:px-10 lg:py-28" data-testid="cash-section">
        <div className="mx-auto grid max-w-7xl items-center gap-16 lg:grid-cols-2 lg:gap-24">
          <div className="lg:order-2">
            <Reveal>
              <Overline tone="orange">{t("home.cash_overline")}</Overline>
              <H2 className="mt-4 text-ostelo-deep">{t("home.cash_title")}</H2>
              <p className="mt-5 max-w-lg text-base leading-relaxed text-ostelo-ink/60">{t("home.cash_sub")}</p>
            </Reveal>
            <div className="mt-10 space-y-6">
              {cashSteps.map((s, i) => (
                <Reveal key={s.title} delay={0.1 + i * 0.1}>
                  <div className="flex items-start gap-5" data-testid={`cash-step-${i}`}>
                    <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-ostelo-deep font-sans text-sm font-extrabold text-ostelo-orange">
                      0{i + 1}
                    </span>
                    <div>
                      <h3 className="font-sans text-lg font-extrabold text-ostelo-deep">{s.title}</h3>
                      <p className="mt-1 text-sm leading-relaxed text-ostelo-ink/60">{s.desc}</p>
                    </div>
                  </div>
                </Reveal>
              ))}
            </div>
          </div>

          <Reveal delay={0.15} className="lg:order-1">
            <div className="relative flex items-center justify-center">
              <img
                src="/assets/characters/cashback-800.webp"
                srcSet="/assets/characters/cashback-500.webp 500w, /assets/characters/cashback-800.webp 800w, /assets/characters/cashback-1536.webp 1536w"
                sizes="(min-width: 1024px) 778px, 100vw"
                width="1536"
                height="1024"
                alt={t("home.cash_visual_alt")}
                loading="lazy"
                className="w-full max-w-none object-contain lg:w-[112%] lg:translate-x-[-6%] xl:w-[118%] 2xl:w-[128%]"
                data-testid="cash-visual"
              />
            </div>
          </Reveal>
        </div>
      </section>

      {/* 8 — BACK-OFFICE HÔTELIER */}
      <section className="bg-white px-6 py-24 lg:px-10 lg:py-28" data-testid="back-section">
        <div className="mx-auto max-w-7xl">
          <div className="grid items-start gap-14 lg:grid-cols-2">
            <div>
              <Reveal>
                <Overline tone="blue">{t("home.back_overline")}</Overline>
                <H2 className="mt-4 text-ostelo-deep">{t("home.back_title")}</H2>
                <p className="mt-5 max-w-lg text-base leading-relaxed text-ostelo-ink/60">{t("home.back_sub")}</p>
              </Reveal>
              <div className="mt-10 grid gap-4 sm:grid-cols-2">
                {backFeatures.map((f, i) => {
                  const Icon = BACK_ICONS[i];
                  return (
                    <Reveal key={f.title} delay={i * 0.07}>
                      <div className="flex h-full items-start gap-3.5 rounded-2xl border border-ostelo-deep/8 bg-[#FAFCFF] p-5" data-testid={`back-feature-${i}`}>
                        <Icon size={17} className="mt-0.5 shrink-0 text-ostelo-blue" />
                        <div>
                          <h3 className="text-sm font-extrabold text-ostelo-deep">{f.title}</h3>
                          <p className="mt-1 text-xs leading-relaxed text-ostelo-ink/55">{f.desc}</p>
                        </div>
                      </div>
                    </Reveal>
                  );
                })}
              </div>
              <Reveal delay={0.3} className="mt-8">
                <p className="inline-flex items-center gap-3 rounded-full bg-ostelo-deep px-5 py-3 text-xs font-bold text-white" data-testid="back-dual">
                  <Smartphone size={14} className="text-ostelo-orange" />
                  {t("home.back_dual")}
                </p>
              </Reveal>
            </div>
            <div className="max-md:hidden lg:pt-16">
              <DashboardMockup />
            </div>
          </div>
        </div>
      </section>

      {/* 9 — CARNET DE VOYAGE / APRÈS-SÉJOUR */}
      <section className="bg-[#F6F9FF] px-6 py-24 lg:px-10 lg:py-28" data-testid="carnet-section">
        <div className="mx-auto grid max-w-7xl items-center gap-16 lg:grid-cols-2">
          <div>
            <Reveal>
              <Overline tone="blue">{t("home.carnet_overline")}</Overline>
              <H2 className="mt-4 text-ostelo-deep">{t("home.carnet_title")}</H2>
              <p className="mt-5 max-w-lg text-base leading-relaxed text-ostelo-ink/60">{t("home.carnet_sub")}</p>
              <p className="mt-4 max-w-lg text-sm font-semibold leading-relaxed text-ostelo-deep/80">{t("home.carnet_sub2")}</p>
            </Reveal>
            <div className="mt-10 grid gap-4 sm:grid-cols-2">
              {carnetPoints.map((p, i) => {
                const Icon = CARNET_ICONS[i];
                return (
                  <Reveal key={p} delay={0.08 * i}>
                    <div className="flex items-center gap-3 rounded-xl border border-ostelo-deep/8 bg-white px-4 py-3.5" data-testid={`carnet-point-${i}`}>
                      <Icon size={15} className="shrink-0 text-ostelo-orange" />
                      <span className="text-sm font-semibold text-ostelo-deep">{p}</span>
                    </div>
                  </Reveal>
                );
              })}
            </div>
            <Reveal delay={0.25} className="mt-8">
              <div className="flex items-start gap-4 rounded-2xl border-l-4 border-ostelo-orange bg-white p-6">
                <HeartHandshake size={18} className="mt-0.5 shrink-0 text-ostelo-orange" />
                <p className="text-sm leading-relaxed text-ostelo-ink/70">
                  {t("home.carnet_fid_a")}{" "}
                  <span className="font-extrabold text-ostelo-deep">{t("home.carnet_fid_b")}</span>
                </p>
              </div>
            </Reveal>
          </div>

          <Reveal delay={0.1}>
            <div className="relative mx-auto max-w-md max-md:hidden" data-testid="carnet-visual">
              <div className="pointer-events-none absolute left-1/2 top-1/2 h-[300px] w-[300px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-ostelo-blue/10 blur-3xl sm:h-[420px] sm:w-[420px]" aria-hidden="true" />

              {/* Trajectoires pointillées — formes graphiques reliées physiquement aux 3 icônes */}
              <div className="pointer-events-none absolute -left-[115px] -top-[155px] z-10 hidden sm:block" aria-hidden="true" data-testid="carnet-trajectories">
                <svg width="670" height="870" viewBox="-115 -155 670 870" fill="none" className="overflow-visible">
                  {/* Avion — grande trajectoire aérienne avec boucle, rejoint le cercle */}
                  <path d="M140 4 C95 -75 185 -140 300 -142 C400 -144 465 -128 484 -98 C512 -112 528 -80 508 -60 C492 -44 472 -56 480 -80 C485 -60 484 -44 478 -30" stroke="#2451E8" strokeOpacity="0.55" strokeWidth="2" strokeDasharray="6 8" strokeLinecap="round" />
                  {/* Appareil photo — large courbe descendante avec boucle, rejoint le cercle */}
                  <path d="M180 625 C115 692 -15 702 -68 662 C-100 638 -102 606 -78 591 C-48 576 -28 592 -43 607 C-58 622 -88 612 -82 592 C-78 579 -60 568 -44 563" stroke="#2451E8" strokeOpacity="0.5" strokeWidth="1.8" strokeDasharray="5 8" strokeLinecap="round" />
                  {/* Pin — courbe latérale en S, rejoint le cercle */}
                  <path d="M444 560 C520 580 560 550 548 512 C538 480 500 462 478 474" stroke="#2451E8" strokeOpacity="0.45" strokeWidth="1.7" strokeDasharray="5 7" strokeLinecap="round" className="hidden sm:block" />
                </svg>
              </div>

              <div className="pointer-events-none absolute -right-4 -top-10 z-10 sm:-right-16 sm:-top-14" aria-hidden="true">
                <span className="flex h-11 w-11 items-center justify-center rounded-full bg-white shadow-[0_10px_25px_rgb(12,27,71,0.16)]">
                  <Plane size={20} className="text-ostelo-blue" />
                </span>
              </div>

              <div className="pointer-events-none absolute -left-4 bottom-12 z-10 sm:-left-14" aria-hidden="true">
                <span className="flex h-9 w-9 items-center justify-center rounded-full bg-white shadow-[0_8px_20px_rgb(12,27,71,0.14)]">
                  <Camera size={15} className="text-ostelo-blue" />
                </span>
              </div>

              <div className="pointer-events-none absolute -right-10 bottom-36 z-10 hidden sm:block" aria-hidden="true">
                <span className="flex h-7 w-7 items-center justify-center rounded-full bg-white shadow-[0_6px_16px_rgb(12,27,71,0.12)]">
                  <MapPin size={12} className="text-ostelo-orange" />
                </span>
              </div>
              <img
                src="/assets/characters/carnet-voyage-448.webp"
                srcSet="/assets/characters/carnet-voyage-448.webp 448w, /assets/characters/carnet-voyage-860.webp 860w"
                sizes="(min-width: 512px) 448px, 100vw"
                width="860"
                height="1208"
                alt="Carnet de voyage Ostelo — interface officielle"
                className="relative w-full rounded-3xl shadow-[0_30px_70px_-20px_rgb(12,27,71,0.25)]"
                loading="lazy"
              />
            </div>
          </Reveal>
        </div>
      </section>

      {/* 10 — PERFORMANCE / BUSINESS */}
      <section className="px-6 pb-24 pt-10 lg:px-10" data-testid="perf-section">
        <div className="relative mx-auto max-w-7xl overflow-hidden rounded-[2.5rem] bg-ostelo-deep px-8 py-16 text-white lg:px-16 lg:py-20">
          <div className="pointer-events-none absolute -left-24 -top-24 h-72 w-72 rounded-full border-[22px] border-ostelo-blue/25" />
          <div className="relative">
            <Reveal>
              <Overline tone="orange">{t("home.perf_overline")}</Overline>
              <H2 className="mt-4 max-w-2xl text-white">{t("home.perf_title")}</H2>
            </Reveal>
            <div className="mt-12 grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
              {perfItems.map((p, i) => {
                const Icon = PERF_ICONS[i];
                return (
                  <Reveal key={p.title} delay={i * 0.07}>
                    <div className="h-full rounded-2xl border border-white/10 bg-white/5 p-6 transition-colors duration-300 hover:bg-white/10" data-testid={`perf-item-${i}`}>
                      <Icon size={18} className="text-ostelo-orange" />
                      <h3 className="mt-3.5 font-sans text-base font-extrabold">{p.title}</h3>
                      <p className="mt-1.5 text-sm leading-relaxed text-white/60">{p.desc}</p>
                    </div>
                  </Reveal>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* 11 — ÉCOSYSTÈME TERRITORIAL */}
      <section className="bg-white px-6 pb-24 pt-[70px] lg:px-10 lg:pb-28 lg:pt-[82px]" data-testid="eco-section">
        <div className="mx-auto max-w-5xl">
          <Reveal className="text-center">
            <Overline tone="blue">{t("home.eco_overline")}</Overline>
            <H2 className="mx-auto mt-4 max-w-2xl text-ostelo-deep">{t("home.eco_title")}</H2>
          </Reveal>

          <div className="mt-14 flex flex-col items-center">
            <Reveal>
              <div className="rounded-2xl bg-ostelo-deep px-10 py-5 font-sans text-2xl font-extrabold lowercase tracking-tight text-white shadow-[0_20px_50px_rgb(12,27,71,0.3)]" data-testid="eco-core">
                ostelo<span className="text-ostelo-orange">.</span>
              </div>
            </Reveal>
            <div className="h-10 border-l-2 border-dashed border-ostelo-blue/30" aria-hidden="true" />

            <div className="grid w-full gap-5 sm:grid-cols-3">
              {ecoCols.map((col, i) => {
                const Icon = ECO_ICONS[i];
                return (
                  <Reveal key={col.title} delay={i * 0.1}>
                    <div className="h-full rounded-2xl border border-ostelo-deep/8 bg-[#FAFCFF] p-6" data-testid={`eco-col-${i}`}>
                      <div className="flex items-center gap-3">
                        <span className={`flex h-10 w-10 items-center justify-center rounded-xl ${["bg-ostelo-sky text-ostelo-blue", "bg-[#FFF1E0] text-ostelo-orange", "bg-ostelo-mint text-ostelo-green"][i]}`}>
                          <Icon size={17} />
                        </span>
                        <h3 className="font-sans text-lg font-extrabold text-ostelo-deep">{col.title}</h3>
                      </div>
                      <ul className="mt-4 space-y-2">
                        {col.items.map((item) => (
                          <li key={item} className="flex items-center gap-2 text-xs font-medium text-ostelo-ink/60">
                            <span className="h-1 w-1 rounded-full bg-ostelo-blue/50" />
                            {item}
                          </li>
                        ))}
                      </ul>
                    </div>
                  </Reveal>
                );
              })}
            </div>

            <div className="h-10 border-l-2 border-dashed border-ostelo-blue/30" aria-hidden="true" />
            <Reveal>
              <div className="rounded-2xl bg-[#F6F9FF] px-6 py-5" data-testid="eco-bottom">
                <p className="mb-4 text-center text-[10px] font-bold uppercase tracking-[0.25em] text-ostelo-ink/35">
                  {t("home.eco_bottom_label")}
                </p>
                <div className="flex flex-wrap items-center justify-center gap-3">
                  {ecoBottom.map((b, i) => {
                    const Icon = ECO_BOTTOM_ICONS[i];
                    return (
                      <span key={b} className="inline-flex items-center gap-2 rounded-full bg-white px-4 py-2.5 text-xs font-bold text-ostelo-deep shadow-sm">
                        <Icon size={14} className="text-ostelo-orange" />
                        {b}
                      </span>
                    );
                  })}
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* 12 — GROUPES & CSE */}
      <section className="px-6 pb-24 lg:px-10" data-testid="groupes-section">
        <Reveal className="mx-auto max-w-7xl">
          <div className="flex flex-wrap items-center justify-between gap-6 rounded-3xl bg-[#F6F9FF] px-8 py-10 lg:px-12">
            <div className="flex items-start gap-5">
              <span className="flex h-12 w-12 shrink-0 items-center justify-center rounded-xl bg-ostelo-deep">
                <Users size={20} className="text-ostelo-orange" />
              </span>
              <div>
                <h2 className="font-sans text-2xl font-extrabold tracking-tight text-ostelo-deep">{t("home.groupes_title")}</h2>
                <p className="mt-2 max-w-xl text-sm leading-relaxed text-ostelo-ink/60">{t("home.groupes_sub")}</p>
              </div>
            </div>
            <Btn to={L("/contact")} variant="ghost" testId="groupes-cta">
              {t("home.groupes_cta")}
              <ArrowRight size={16} />
            </Btn>
          </div>
        </Reveal>
      </section>

      {/* 13 — CTA FINAL */}
      <CtaSection product title={t("home.cta_final_title")} sub={t("home.cta_final_sub")} />
    </>
  );
}
